import { Divider, FormHelperText, IconButton, InputBase, TablePagination } from "@mui/material";
import * as React from "react";
import DirectionsIcon from "@mui/icons-material/Directions";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; 
import { styled } from '@mui/material/styles';

// Root container for pagination
const PaginationContainer = styled('div')(() => ({
  padding: "2px 4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
}));

// Custom input styling
const PageInput = styled(InputBase)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  width: 150,
}));

// Go button styling
const GoButton = styled(IconButton)(({ theme }) => ({
  padding: 10,
}));

// Vertical divider styling
const VerticalDivider = styled(Divider)(({ theme }) => ({
  height: 15,
  margin: 4,
}));

interface Props {
  count: number;
  rowsPerPageOptions?: Array<number | { value: number; label: string }>;
  pageSize: number;
}

export const GoToPagination: React.FC<Props> = ({
  count,
  rowsPerPageOptions,
  pageSize,
}) => {
  const [inputPage, setInputPage] = useState<number | null>(null);
  const [error, setError] = useState<String | null>();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current page from URL
  const params = new URLSearchParams(location.search);
  const currentPage = parseInt(params.get("page") || "0");

  const changePage = () => {
    if (inputPage !== null && inputPage >= 0) {
      handlePageChange(null, inputPage);
    }
  };

  const onInputChange = (value: string) => {
    var parsed = parseInt(value) - 1;
    if (isNaN(parsed)) {
      setError("Prašome įvesti skaičių");
      setInputPage(null);
    } else if (parsed > Math.ceil(count / pageSize) - 1) {
      setError(`Klaida! Maksimalus puslapio numeris yra: ${Math.ceil(count / pageSize)}`);
      setInputPage(null);
    } else if (parsed < 0) {
      setError(`Klaida! Minimalus puslapio numeris yra: 1`);
      setInputPage(null);
    } else {
      setError(null);
      setInputPage(parsed);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    const params = new URLSearchParams(location.search);
    params.set("page", newPage.toString());
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newSize = parseInt(event.target.value, 10);
    const params = new URLSearchParams(location.search);
    params.set("size", newSize.toString());
    params.set("page", "0"); // Reset page when size changes
    navigate({ search: params.toString() }, { replace: true });
  };

  return (
    <PaginationContainer>
      {error && <FormHelperText error>{error}</FormHelperText>}
      <PageInput
        type="number"
        placeholder="Eiti į puslapį"
        inputProps={{
          "aria-label": "eiti į puslapį",
          min: 1,
          max: Math.ceil(count / pageSize),
        }}
        onChange={(e) => onInputChange(e.target.value)}
      />
      <VerticalDivider orientation="vertical" />
      <GoButton
        color="default"
        aria-label="directions"
        onClick={changePage}
        disabled={!!error || inputPage === null}
        size="large">
        <DirectionsIcon />
      </GoButton>
      <TablePagination
        labelRowsPerPage={"Puslapio dydis"}
        component="div"
        size="small"
        rowsPerPageOptions={rowsPerPageOptions || [10, 25, 50]}
        rowsPerPage={pageSize}
        count={count}
        page={currentPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </PaginationContainer>
  );
};
